// root '../../../';

// dependencies
import { Child } from 'pym.js';
import { select } from 'd3-selection';
import '@fontsource-variable/noto-serif/wght.css';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)
import { isAppPlatform, isDark } from './utils/weatherApp.js';
// utils & graphic
import config from './config';
import graphic from './graphic';

// styles
import './styles/index.scss';

/* develblock:start */
import meta from './data/meta.json';

if (isAppPlatform()) {
  document.documentElement.classList.add('platform-app');
  document.body.classList.add('platform-app');
}

if (isDark()) {
  document.documentElement.classList.add('dark-true');
  document.body.classList.add('dark-true');
}

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

const DATA_URL = 'https://ix.cnn.io/data/wildfire-tracker/historical_wildfire_data.json';

let processedData = null;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ renderCallback: draw, id: pkg.name });

// URL param visibility toggles (default: visible)
const params = new URLSearchParams(window.location.search);
if (params.get('show_source') === 'false') {
  document.getElementById('source').hidden = true;
}
if (params.get('show_credit') === 'false') {
  document.getElementById('credit').hidden = true;
}

window.onload = async () => {
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  try {
    const response = await fetch(DATA_URL);
    const json = await response.json();
    processedData = parseWildfireData(json);

    const footnote = document.querySelector('.footnote');
    if (footnote && json.lastUpdatedStr) {
      footnote.textContent = `Updated: ${json.lastUpdatedStr}.`;
    }

    draw();
    window.pymChild.sendHeight();
  } catch (err) {
    console.error('Failed to load wildfire data:', err);
  }
};

/**
 * Groups raw weekly cumulative data into per-year series.
 * Each value gets a `normalizedDate` using 2020 (a leap year) so
 * all years share the same x-axis scale.
 */
function parseWildfireData(json) {
  const byYear = {};

  json.data.forEach((d) => {
    const [yearStr, monthStr, dayStr] = d.report_date.split('-');
    const year = +yearStr;
    const month = +monthStr;
    const day = +dayStr;

    if (!byYear[year]) byYear[year] = [];

    byYear[year].push({
      year,
      date: new Date(year, month - 1, day),
      normalizedDate: new Date(2020, month - 1, day), // fixed year for x-axis alignment
      acres: +d.total_acres,
      fires: +d.total_fires,
    });
  });

  return Object.values(byYear)
    .map(values => ({
      year: values[0].year,
      values: values.sort((a, b) => a.date - b.date),
    }))
    .sort((a, b) => a.year - b.year);
}

function draw(initialWidth) {
  if (!processedData) return;

  const container = select('#graphic');
  container.html('');

  const fallbackWidth = container.node().clientWidth || 600;
  const safeWidth = initialWidth !== fallbackWidth ? fallbackWidth : initialWidth;
  const isMobile = safeWidth <= 500;

  const heightMultiplier = isMobile ? 0.75 : 0.48;
  const initialHeight = heightMultiplier * safeWidth;

  const props = { initialWidth: safeWidth, initialHeight, isMobile };

  const graphicConfig = config(props);
  graphicConfig.data = processedData;

  graphic(graphicConfig, container, isMobile);
}
